// stores/exceptionsStore.js
import { defineStore } from "pinia";
import Api from "src/utils/api";
import Constants from "src/utils/constants";
import { useUserStore } from "src/stores/userStore";
import { useAppSettingsStore } from "src/stores/appSettingsStore";
import Helper from "src/utils/helper";
import { toRaw } from "vue";
import { useFormTemplateResponseStore } from "src/stores/formTemplateResponseStore";
const tableName = Constants.enums.TABLES_NAME_ENUM.EXCEPTIONS_TABLE;

export const useExceptionsStore = defineStore("exceptions_store", {
  persist: true,
  state: () => ({
    exceptionsList: [],
    isLoading: false
  }),
  actions: {
    async logException(error, additionalData = {}) {
      const formTemplateResponseStore = useFormTemplateResponseStore();
      try {
        this.isLoading = true;
        const userStore = useUserStore();

        // Get version information
        const versionInfo = await this.getVersionInfo();

        const exceptionData = {
          // User Information
          user: {
            id: userStore.userData?.id || null,
            email: userStore.userData?.email || null,
            full_name: userStore.userData?.full_name || null,
            role: userStore.userData?.role || null,
            selected_company: userStore.userData?.selected_company || null
          },

          // Error Information
          error_message: error.message || error.toString(),
          error_type: error.name || "Error",
          stack_trace: error.stack || null,

          // Environment Information
          environment: import.meta.env.VITE_ENVIRONMENT || "development",
          platform: versionInfo.platform,
          current_version: versionInfo.current_version,
          latest_version: versionInfo.latest_version,
          has_update_available: versionInfo.has_update_available,
          device_name: this.getDeviceName(),
          current_data_object: toRaw(formTemplateResponseStore.response),
          network:
            JSON.parse(JSON.stringify(navigator.connection)) || "Unknown",

          // Session Information
          session: Helper.isSessionExpiringSoon(),
          user_agent:
            typeof navigator !== "undefined" ? navigator.userAgent : null,
          screen_resolution:
            typeof screen !== "undefined"
              ? `${screen.width}x${screen.height}`
              : null,
          window_size:
            typeof window !== "undefined"
              ? `${window.innerWidth}x${window.innerHeight}`
              : null,
          timezone:
            typeof Intl !== "undefined"
              ? Intl.DateTimeFormat().resolvedOptions().timeZone
              : null,
          language:
            typeof navigator !== "undefined" ? navigator.language : null,
          online_status:
            typeof navigator !== "undefined" ? navigator.onLine : null,
          timestamp: new Date().toISOString(),

          // Additional Context
          additional_data: additionalData
        };

        this.exceptionsList.push(exceptionData);
        console.log("TOTAL EXCEPTION DATA :", this.exceptionsList.length);

        console.log("CHECKING FOR THE OLDER DATA SIZE");
        // here would like to remove the data that are more than 2 days old to not get lots of data saved in the database
        this.exceptionsList = this.exceptionsList.filter(
          (exception) =>
            new Date(exception.timestamp) >
            new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
        );
        console.log(
          "CHECKING FOR THE OLDER DATA SIZE DONE------",
          this.exceptionsList.length
        );

        // const result = await Api.Exceptions.logException(exceptionData);

        // if (result.error) {
        //   console.error("Failed to log exception to database:", result.error);
        // }

        // return result;
      } catch (logError) {
        console.error("Error in logException:", logError);
        return {
          error: logError.message || "An unexpected error occurred",
          id: null
        };
      } finally {
        this.isLoading = false;
      }
    },

    async getVersionInfo() {
      try {
        const appSettingsStore = useAppSettingsStore();

        // Get current version
        const currentVersion = Helper.getCurrentVersion();

        // Fetch app settings if not already loaded
        if (Object.keys(appSettingsStore.app_settings).length === 0) {
          // ot's risky if the collection doesn't exists on local
          // await appSettingsStore.fetchAppSettings();
        }

        // Get platform and corresponding version key
        const platform = this.getPlatform();
        const versionKey = this.getVersionKeyForPlatform(platform);
        const latestVersion = appSettingsStore.getAppSettings(versionKey);

        // Check if update is available
        let hasUpdateAvailable = false;
        if (latestVersion && currentVersion !== "N/A") {
          const comparison = Helper.compareVersions(
            latestVersion,
            currentVersion
          );
          hasUpdateAvailable = comparison > 0;
        }

        return {
          current_version: currentVersion,
          latest_version: latestVersion,
          has_update_available: hasUpdateAvailable,
          platform: platform,
          version_key: versionKey
        };
      } catch (error) {
        console.warn("Could not fetch version information:", error);
        return {
          current_version: Helper.getCurrentVersion(),
          latest_version: null,
          has_update_available: false,
          platform: this.getPlatform(),
          version_key: null,
          error: error.message
        };
      }
    },

    getPlatform() {
      // Detect platform
      if (typeof window !== "undefined") {
        const userAgent = navigator.userAgent.toLowerCase();
        if (/android/.test(userAgent)) {
          return "android";
        } else if (/iphone|ipad|ipod/.test(userAgent)) {
          return "ios";
        } else {
          return "web";
        }
      }
      return "unknown";
    },

    getVersionKeyForPlatform(platform) {
      const appSettings = Constants.enums.APP_SETTINGS;
      switch (platform) {
        case "android":
          return appSettings.CURRENT_ANDROID_VERSION;
        case "ios":
          return appSettings.CURRENT_IOS_VERSION;
        case "web":
        default:
          return appSettings.CURRENT_WEB_VERSION;
      }
    },

    async isUpdateAvailable() {
      try {
        const versionInfo = await this.getVersionInfo();
        return versionInfo.has_update_available;
      } catch (error) {
        console.warn("Could not check for updates:", error);
        return false;
      }
    },

    async getLatestVersion() {
      try {
        const versionInfo = await this.getVersionInfo();
        return versionInfo.latest_version;
      } catch (error) {
        console.warn("Could not get latest version:", error);
        return null;
      }
    },

    // get device name for android
    getAndroidDeviceName() {
      const androidUserAgentString = window.navigator.userAgent.slice(
        window.navigator.userAgent.indexOf("Android")
      );
      const androidDeviceName = androidUserAgentString.slice(
        androidUserAgentString.indexOf("; ") + 1,
        androidUserAgentString.indexOf(")")
      );
      if (androidDeviceName) {
        return androidDeviceName.trim().split(" ")[0];
      }

      return "Android";
    },

    // get device name for ios
    getIosDeviceName() {
      const screenResolution = `${window.screen.width}x${window.screen.height}`;
      const device =
        Constants.enums.DEVICE_MAPPING.IOS_DEVICE_MAPPING.get(screenResolution);
      if (device) {
        return device;
      }
      return "Iphone";
    },

    // get device name for desktop
    getDesktopDeviceName() {
      const platform =
        navigator?.userAgentData?.platform || navigator?.platform || "unknown";
      const device =
        Constants.enums.DEVICE_MAPPING.DESKTOP_DEVICE_MAPPING.get(platform) ??
        "Unknown";
      return device;
    },

    // get device name utility
    getDeviceName() {
      let device = "";

      // check if mobile device
      const isMobileDevice = window.navigator.userAgent
        .toLowerCase()
        .includes("mobi");

      if (isMobileDevice) {
        if (window.navigator.userAgent.includes("Android")) {
          device = this.getAndroidDeviceName();
        } else {
          device = this.getIosDeviceName();
        }
      } else {
        device = this.getDesktopDeviceName();
      }

      return device;
    },

    async getExceptions(config = {}) {
      try {
        this.isLoading = true;
        const result = await Api.Exceptions.getExceptions({
          tableName,
          sortField: "created_at",
          sortOrder: -1,
          fetchAll: true,
          ...config
        });

        if (result && result.data) {
          this.exceptionsList = result.data;
        }

        return result;
      } catch (error) {
        console.error("Error fetching exceptions:", error);
        return null;
      } finally {
        this.isLoading = false;
      }
    },

    async clearExceptions() {
      this.exceptionsList = [];
    }
  },
  $reset() {
    this.exceptionsList = [];
    this.isLoading = false;
  }
});
